'use server'

import { AuthService } from '@/lib/services/auth.service'
import { EmailService } from '@/lib/services/email.service'
import { RegisterInput, LoginInput, AuthResponse } from '@/lib/types/auth.types'
import { createSession, deleteSession, getSession } from '@/lib/utils/session'
import { LogService } from '@/lib/services/log.service'
import { cookies } from 'next/headers'
import { redirect } from 'next/navigation'

const SESSION_COOKIE_NAME = 'session'
const SESSION_MAX_AGE = 60 * 60 * 24 * 7 // 7 días

import { sendWelcomeMessage } from './messages';

/**
 * Server Action: Registrar un nuevo usuario
 */
export async function register(input: RegisterInput): Promise<AuthResponse> {
  try {
    // Registrar el usuario
    const user = await AuthService.register(input)

    // Crear sesión
    const sessionToken = await createSession(user.id)

    // Establecer cookie de sesión
    const cookieStore = await cookies()
    cookieStore.set(SESSION_COOKIE_NAME, sessionToken, {
      httpOnly: true,
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'lax',
      maxAge: SESSION_MAX_AGE,
      path: '/',
    })

    // Registrar log de nuevo usuario
    await LogService.recordLog(user.id, 'Registro de nuevo usuario')

    // Enviar mensaje de bienvenida interno
    await sendWelcomeMessage(user.id);

    return {
      success: true,
      data: user,
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error al registrar el usuario',
    }
  }
}

/**
 * Server Action: Iniciar sesión
 */
export async function login(input: LoginInput): Promise<AuthResponse> {
  try {
    // Autenticar el usuario
    const user = await AuthService.login(input.email, input.password)

    // Crear sesión
    const sessionToken = await createSession(user.id)

    // Establecer cookie de sesión
    const cookieStore = await cookies()
    cookieStore.set(SESSION_COOKIE_NAME, sessionToken, {
      httpOnly: true,
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'lax',
      maxAge: SESSION_MAX_AGE,
      path: '/',
    })

    // Registrar log de inicio de sesión
    await LogService.recordLog(user.id, 'Inicio de sesión')

    return {
      success: true,
      data: user,
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error al iniciar sesión',
    }
  }
}

/**
 * Server Action: Cerrar sesión
 */
export async function logout(): Promise<{ success: boolean; error?: string }> {
  try {
    const cookieStore = await cookies()
    const sessionToken = cookieStore.get(SESSION_COOKIE_NAME)?.value

    if (sessionToken) {
      // Obtener userId antes de borrar la sesión para el log
      const session = await getSession()
      if (session) {
        await LogService.recordLog(session.userId, 'Cierre de sesión')
      }

      // Eliminar sesión de la base de datos
      await deleteSession(sessionToken)
    }

    // Eliminar cookie
    cookieStore.delete(SESSION_COOKIE_NAME)

    return {
      success: true,
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error al cerrar sesión',
    }
  }
}

/**
 * Server Action: Cerrar sesión y redirigir
 */
export async function logoutAndRedirect(redirectTo: string = '/') {
  await logout()
  redirect(redirectTo)
}

/**
 * Server Action: Cerrar sesión y redirigir (para uso en formularios)
 */
export async function handleLogout() {
  await logout()
  redirect('/')
}

/**
 * Server Action: Enviar correo de restablecimiento de contraseña
 */
export async function sendPasswordResetEmail(email: string): Promise<{ success: boolean; error?: string }> {
  try {
    const token = await AuthService.generatePasswordResetToken(email)
    const result = await EmailService.sendPasswordResetEmail(email, token)

    if (!result.success) {
      throw new Error('Error al enviar el correo electrónico')
    }

    return { success: true }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error al procesar la solicitud',
    }
  }
}

/**
 * Server Action: Restablecer contraseña
 */
export async function updatePassword(token: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
  try {
    await AuthService.resetPassword(token, newPassword)
    return { success: true }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error al restablecer la contraseña',
    }
  }
}

/**
 * Server Action: Cambiar contraseña de usuario autenticado
 */
export async function changePasswordAction(oldPassword: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
  try {
    const session = await getSession()
    if (!session) {
      throw new Error('No hay una sesión activa')
    }

    await AuthService.changePassword(session.userId, oldPassword, newPassword)

    // Opcional: Registrar log
    await LogService.recordLog(session.userId, 'Cambio de contraseña')

    return { success: true }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error al cambiar la contraseña',
    }
  }
}
